/**
 * Response type from the detokenization API
 */
export interface DetokenizeResult {
  success: boolean;
  /** The real ph_distinct_id */
  value?: string;
  /** ISO 8601 timestamp when token was created */
  timestamp?: string;
  error?: string;
}

const DETOKENIZE_TIMEOUT_MS = 8000;

const isDev = process.env.REACT_APP_DEPLOY_ENV === 'local'
  || process.env.REACT_APP_DEPLOY_ENV === 'dev';

const TOKENIZER_API_URL = isDev
  ? 'https://vercel-token-git-develop-bighealth.vercel.app'
  : 'https://vercel-token-two.vercel.app';

/**
 * Detokenizes a PostHog token by calling the Vercel API.
 *
 * @param token - The ph_token to detokenize
 * @returns Promise resolving to DetokenizeResult
 */
export const detokenize = async (token: string): Promise<DetokenizeResult> => {
  if (!TOKENIZER_API_URL) {
    // eslint-disable-next-line no-console
    console.error('TOKENIZER_API_URL not configured');
    return { success: false, error: 'Tokenizer not configured' };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DETOKENIZE_TIMEOUT_MS);

  try {
    const response = await fetch(`${TOKENIZER_API_URL}/api/detokenize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      return {
        success: false,
        error: `Detokenization failed with status ${response.status}`,
      };
    }

    const data: DetokenizeResult = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      return {
        success: false,
        error: `Detokenization timed out after ${DETOKENIZE_TIMEOUT_MS}ms`,
      };
    }

    // eslint-disable-next-line no-console
    console.error('Detokenization error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
};
